// Administration ▸ Automatisation : règles d'attribution automatique,
// segmentées par concession. Les règles sont des données (table
// automatisation_regles) ; le catalogue des déclencheurs / champs / opérateurs
// / actions est servi par le backend (services/automatisation.js).
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import './Automatisation.css';

const REGLE_VIDE = {
  concession_id: '',
  module: 'leads_web',
  nom: '',
  conditions: [],
  action: 'attribuer_vendeur',
  cible_user_id: '',
  ordre: '',
  actif: true,
};

function nomUtilisateur(u) {
  return `${u.prenom || ''} ${u.nom || ''}`.trim() || `#${u.id}`;
}

function Automatisation({ user, editMode = false, setHeaderAction }) {
  const [tab, setTab] = useState('regles');
  const [catalogue, setCatalogue] = useState({ modules: {}, operateurs: {} });
  const [regles, setRegles] = useState([]);
  const [concessions, setConcessions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [filtreConcession, setFiltreConcession] = useState('');
  const [filtreModule, setFiltreModule] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(REGLE_VIDE);
  const [editingId, setEditingId] = useState(null);
  const [tousLesCollaborateurs, setTousLesCollaborateurs] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [testConcession, setTestConcession] = useState('');
  const [testModule, setTestModule] = useState('leads_web');
  const [testObjet, setTestObjet] = useState({});
  const [testResultat, setTestResultat] = useState(null);

  const canDelete = editMode && user?.role === 'super-admin';

  const load = useCallback(async () => {
    try {
      const [cat, reg, conc, usr] = await Promise.all([
        api.get('/automatisation/catalogue'),
        api.get('/automatisation/regles'),
        api.get('/concessions'),
        api.get('/users'),
      ]);
      setCatalogue(cat.data);
      setRegles(reg.data.regles || []);
      setConcessions(conc.data.concessions || []);
      setUsers(usr.data.users || []);
      setError('');
    } catch {
      setError('Erreur de chargement des règles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const ouvrirCreation = useCallback(() => {
    setEditingId(null);
    setForm({ ...REGLE_VIDE, concession_id: filtreConcession || '' });
    setTousLesCollaborateurs(false);
    setFormError('');
    setShowModal(true);
  }, [filtreConcession]);

  useEffect(() => {
    if (setHeaderAction) {
      setHeaderAction({ primary: { label: '+ Nouvelle règle', onClick: ouvrirCreation } });
    }
    return () => { if (setHeaderAction) setHeaderAction(null); };
  }, [setHeaderAction, ouvrirCreation]);

  useEffect(() => { if (!editMode) setSelectedIds(new Set()); }, [editMode]);

  const libelleCondition = useCallback((module, c) => {
    const champ = catalogue.modules[module]?.champs?.[c.champ]?.label || c.champ;
    const op = catalogue.operateurs[c.operateur] || c.operateur;
    return `${champ} ${op} « ${c.valeur} »`;
  }, [catalogue]);

  const reglesFiltrees = useMemo(() => {
    const q = search.trim().toLowerCase();
    return regles.filter(r => {
      if (filtreConcession && String(r.concession_id) !== String(filtreConcession)) return false;
      if (filtreModule && r.module !== filtreModule) return false;
      if (!q) return true;
      const texte = [
        r.nom, r.concession_nom, r.cible_prenom, r.cible_nom,
        ...(r.conditions || []).map(c => libelleCondition(r.module, c)),
      ].join(' ').toLowerCase();
      return texte.includes(q);
    });
  }, [regles, search, filtreConcession, filtreModule, libelleCondition]);

  // Règles d'un même groupe (concession × déclencheur), dans l'ordre d'évaluation.
  const groupe = useCallback((r) => regles
    .filter(x => x.concession_id === r.concession_id && x.module === r.module)
    .sort((a, b) => a.ordre - b.ordre || a.id - b.id), [regles]);

  const deplacer = async (r, sens) => {
    const liste = groupe(r);
    const i = liste.findIndex(x => x.id === r.id);
    const j = i + sens;
    if (j < 0 || j >= liste.length) return;
    [liste[i], liste[j]] = [liste[j], liste[i]];
    try {
      // Renumérotation par pas de 10 : ne réécrit que les règles qui bougent.
      await Promise.all(liste.map((x, idx) => (
        x.ordre === (idx + 1) * 10 ? null : api.put(`/automatisation/regles/${x.id}`, { ordre: (idx + 1) * 10 })
      )));
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors du réordonnancement');
    }
  };

  const basculerActif = async (r) => {
    try {
      await api.put(`/automatisation/regles/${r.id}`, { actif: !r.actif });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la mise à jour');
    }
  };

  const ouvrirEdition = (r) => {
    setEditingId(r.id);
    setForm({
      concession_id: r.concession_id,
      module: r.module,
      nom: r.nom,
      conditions: (r.conditions || []).map(c => ({ ...c })),
      action: r.action,
      cible_user_id: r.cible_user_id || '',
      ordre: r.ordre,
      actif: r.actif,
    });
    const cible = users.find(u => u.id === r.cible_user_id);
    setTousLesCollaborateurs(!!cible && cible.concession_id != null && cible.concession_id !== r.concession_id);
    setFormError('');
    setShowModal(true);
  };

  const enregistrer = async () => {
    setSaving(true);
    setFormError('');
    try {
      const body = { ...form };
      if (body.ordre === '' || body.ordre == null) {
        // Nouvelle règle : placée juste avant une éventuelle règle « tout le
        // reste » (sans condition) du groupe, sinon en fin de liste.
        const liste = regles
          .filter(x => String(x.concession_id) === String(body.concession_id) && x.module === body.module && x.id !== editingId)
          .sort((a, b) => a.ordre - b.ordre);
        const fourreTout = liste.find(x => (x.conditions || []).length === 0);
        const max = liste.length ? liste[liste.length - 1].ordre : 0;
        body.ordre = body.conditions.length > 0 && fourreTout ? fourreTout.ordre - 1 : max + 10;
      }
      if (editingId) await api.put(`/automatisation/regles/${editingId}`, body);
      else await api.post('/automatisation/regles', body);
      setShowModal(false);
      await load();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const supprimer = async (ids) => {
    if (!ids.length) return;
    if (!window.confirm(`Supprimer ${ids.length} règle(s) ? Cette action est irréversible.`)) return;
    try {
      if (ids.length === 1) await api.delete(`/automatisation/regles/${ids[0]}`);
      else await api.post('/automatisation/regles/bulk-delete', { ids });
      setSelectedIds(new Set());
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const tester = async () => {
    try {
      const { data } = await api.post('/automatisation/tester', {
        concession_id: testConcession, module: testModule, objet: testObjet,
      });
      setTestResultat({ regle: data.regle });
    } catch (err) {
      setTestResultat({ erreur: err.response?.data?.error || 'Erreur lors du test' });
    }
  };

  const moduleForm = catalogue.modules[form.module] || { champs: {}, actions: {} };
  const collaborateursProposes = users.filter(u => tousLesCollaborateurs
    || !form.concession_id
    || u.concession_id == null
    || String(u.concession_id) === String(form.concession_id));

  const majCondition = (idx, patch) => setForm(f => ({
    ...f,
    conditions: f.conditions.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
  }));

  const allSelected = reglesFiltrees.length > 0 && reglesFiltrees.every(r => selectedIds.has(r.id));

  if (loading) return <div className="page-container"><p>Chargement…</p></div>;

  return (
    <div className="page-container">
      <div className="page-tabs">
        <button className={tab === 'regles' ? 'active' : ''} onClick={() => setTab('regles')}>📋 Règles</button>
        <button className={tab === 'simulateur' ? 'active' : ''} onClick={() => setTab('simulateur')}>🧪 Simulateur</button>
      </div>

      {error && <div className="status-error auto-message">{error}</div>}

      {tab === 'regles' && <>
        <div className="page-toolbar">
          <input
            className="page-search"
            placeholder="🔍 Rechercher..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select
            className={`page-select${filtreConcession ? ' active-filter' : ''}`}
            value={filtreConcession}
            onChange={e => setFiltreConcession(e.target.value)}
          >
            <option value="">Toutes les concessions</option>
            {concessions.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
          </select>
          <select
            className={`page-select${filtreModule ? ' active-filter' : ''}`}
            value={filtreModule}
            onChange={e => setFiltreModule(e.target.value)}
          >
            <option value="">Tous les déclencheurs</option>
            {Object.entries(catalogue.modules).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
          </select>
        </div>

        <p className="auto-aide">
          Pour chaque concession, les règles sont évaluées de haut en bas : la première dont toutes
          les conditions sont vraies s'applique. Une règle sans condition capte « tout le reste ».
          Sans règle applicable, l'affectation round-robin habituelle prend le relais.
        </p>

        {canDelete && selectedIds.size > 0 && (
          <div className="auto-bulk-bar">
            <span>{selectedIds.size} règle(s) sélectionnée(s)</span>
            <button className="btn-secondary" onClick={() => setSelectedIds(new Set())}>✕ Désélectionner</button>
            <button className="btn-danger" onClick={() => supprimer([...selectedIds])}>🗑️ Supprimer la sélection</button>
          </div>
        )}

        <div className="table-container">
          <table>
            <thead>
              <tr>
                {canDelete && (
                  <th className="auto-col-check">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() => setSelectedIds(allSelected ? new Set() : new Set(reglesFiltrees.map(r => r.id)))}
                      title="Tout sélectionner"
                    />
                  </th>
                )}
                <th>Ordre</th>
                <th>Concession</th>
                <th>Règle</th>
                <th>Conditions</th>
                <th>Action</th>
                <th>Statut</th>
                <th>Appliquée</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {reglesFiltrees.length === 0 && (
                <tr><td colSpan={canDelete ? 9 : 8} className="auto-vide">Aucune règle. Utilisez « + Nouvelle règle » pour en créer une.</td></tr>
              )}
              {reglesFiltrees.map(r => {
                const liste = groupe(r);
                const pos = liste.findIndex(x => x.id === r.id);
                const actionLabel = catalogue.modules[r.module]?.actions?.[r.action]?.label || r.action;
                return (
                  <tr key={r.id} className={r.actif ? '' : 'auto-inactive'}>
                    {canDelete && (
                      <td className="auto-col-check">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(r.id)}
                          onChange={() => setSelectedIds(prev => {
                            const n = new Set(prev);
                            if (n.has(r.id)) n.delete(r.id); else n.add(r.id);
                            return n;
                          })}
                        />
                      </td>
                    )}
                    <td>
                      <div className="auto-ordre">
                        <span>{pos + 1}</span>
                        <button className="btn-icon" title="Monter" disabled={pos === 0} onClick={() => deplacer(r, -1)}>↑</button>
                        <button className="btn-icon" title="Descendre" disabled={pos === liste.length - 1} onClick={() => deplacer(r, 1)}>↓</button>
                      </div>
                    </td>
                    <td>{r.concession_nom}</td>
                    <td>
                      <div className="auto-nom">{r.nom}</div>
                      <div className="auto-sous">{catalogue.modules[r.module]?.label || r.module}</div>
                    </td>
                    <td>
                      {(r.conditions || []).length === 0
                        ? <span className="badge-pill badge-neutral">Tout le reste</span>
                        : (r.conditions || []).map((c, i) => (
                          <div key={i} className="auto-condition">{i > 0 && <em>et </em>}{libelleCondition(r.module, c)}</div>
                        ))}
                    </td>
                    <td>
                      {actionLabel} <strong>{r.cible_user_id ? `${r.cible_prenom || ''} ${r.cible_nom || ''}`.trim() : '—'}</strong>
                      {r.cible_user_id && !r.cible_actif && <div className="auto-alerte">⚠️ Collaborateur inactif : règle ignorée</div>}
                      {!r.cible_user_id && <div className="auto-alerte">⚠️ Aucune cible : règle ignorée</div>}
                    </td>
                    <td>
                      <button
                        className={`badge-pill ${r.actif ? 'badge-success' : 'badge-neutral'} auto-toggle`}
                        title={r.actif ? 'Désactiver' : 'Activer'}
                        onClick={() => basculerActif(r)}
                      >
                        {r.actif ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td>
                      <div>{r.nb_applications || 0} fois</div>
                      {r.derniere_application && (
                        <div className="auto-sous">dernière : {new Date(r.derniere_application).toLocaleString('fr-FR')}</div>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn-icon" title="Modifier" onClick={() => ouvrirEdition(r)}>✏️</button>
                        {canDelete && (
                          <button className="btn-icon delete" title="Supprimer" onClick={() => supprimer([r.id])}>🗑️</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </>}

      {tab === 'simulateur' && (
        <div className="auto-simulateur">
          <p className="auto-aide">
            Simulez l'arrivée d'un objet pour vérifier quelle règle s'appliquerait. Rien n'est enregistré.
          </p>
          <div className="auto-sim-grille">
            <label>Concession
              <select value={testConcession} onChange={e => { setTestConcession(e.target.value); setTestResultat(null); }}>
                <option value="">— Choisir —</option>
                {concessions.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </label>
            <label>Déclencheur
              <select value={testModule} onChange={e => { setTestModule(e.target.value); setTestObjet({}); setTestResultat(null); }}>
                {Object.entries(catalogue.modules).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
              </select>
            </label>
            {Object.entries(catalogue.modules[testModule]?.champs || {}).map(([k, ch]) => (
              <label key={k}>{ch.label}
                <input
                  value={testObjet[k] || ''}
                  onChange={e => { setTestObjet(o => ({ ...o, [k]: e.target.value })); setTestResultat(null); }}
                />
              </label>
            ))}
          </div>
          <button className="btn-primary" disabled={!testConcession} onClick={tester}>Tester</button>
          {testResultat && (
            <div className={`auto-message ${testResultat.regle ? 'status-success' : testResultat.erreur ? 'status-error' : 'status-warning'}`}>
              {testResultat.erreur
                || (testResultat.regle
                  ? <>Règle « {testResultat.regle.nom} » → {catalogue.modules[testModule]?.actions?.[testResultat.regle.action]?.label} <strong>{testResultat.regle.cible_prenom} {testResultat.regle.cible_nom}</strong></>
                  : 'Aucune règle ne s\'applique : l\'affectation habituelle (round-robin si activé) prendrait le relais.')}
            </div>
          )}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingId ? 'Modifier la règle' : 'Nouvelle règle'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              {formError && <div className="status-error auto-message">{formError}</div>}
              <div className="form-group">
                <label>Nom de la règle *</label>
                <input
                  value={form.nom}
                  placeholder="ex. Leads Saint-Martin"
                  onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
                />
              </div>
              <div className="auto-form-ligne">
                <div className="form-group">
                  <label>Concession *</label>
                  <select value={form.concession_id} onChange={e => setForm(f => ({ ...f, concession_id: e.target.value }))}>
                    <option value="">— Choisir —</option>
                    {concessions.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Déclencheur *</label>
                  <select
                    value={form.module}
                    onChange={e => {
                      const m = e.target.value;
                      setForm(f => ({ ...f, module: m, conditions: [], action: Object.keys(catalogue.modules[m]?.actions || {})[0] || '' }));
                    }}
                  >
                    {Object.entries(catalogue.modules).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Conditions (toutes doivent être vraies)</label>
                {form.conditions.length === 0 && (
                  <div className="auto-sous">Aucune condition : la règle s'applique à tout ce qui n'a pas été capté par une règle placée au-dessus.</div>
                )}
                {form.conditions.map((c, idx) => (
                  <div key={idx} className="auto-condition-ligne">
                    <select value={c.champ} onChange={e => majCondition(idx, { champ: e.target.value })}>
                      {Object.entries(moduleForm.champs).map(([k, ch]) => <option key={k} value={k}>{ch.label}</option>)}
                    </select>
                    <select value={c.operateur} onChange={e => majCondition(idx, { operateur: e.target.value })}>
                      {Object.entries(catalogue.operateurs).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                    <input
                      value={c.valeur}
                      placeholder={moduleForm.champs[c.champ]?.type === 'telephone' ? 'ex. +1721' : 'Valeur'}
                      onChange={e => majCondition(idx, { valeur: e.target.value })}
                    />
                    <button
                      className="btn-icon delete"
                      title="Retirer la condition"
                      onClick={() => setForm(f => ({ ...f, conditions: f.conditions.filter((_, i) => i !== idx) }))}
                    >🗑️</button>
                  </div>
                ))}
                <button
                  className="btn-secondary"
                  onClick={() => setForm(f => ({
                    ...f,
                    conditions: [...f.conditions, {
                      champ: Object.keys(moduleForm.champs)[0], operateur: 'commence_par', valeur: '',
                    }],
                  }))}
                >+ Ajouter une condition</button>
              </div>

              <div className="auto-form-ligne">
                <div className="form-group">
                  <label>Action *</label>
                  <select value={form.action} onChange={e => setForm(f => ({ ...f, action: e.target.value }))}>
                    {Object.entries(moduleForm.actions).map(([k, a]) => <option key={k} value={k}>{a.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Collaborateur *</label>
                  <select value={form.cible_user_id} onChange={e => setForm(f => ({ ...f, cible_user_id: e.target.value }))}>
                    <option value="">— Choisir —</option>
                    {collaborateursProposes.map(u => <option key={u.id} value={u.id}>{nomUtilisateur(u)}</option>)}
                  </select>
                  <label className="auto-check">
                    <input
                      type="checkbox"
                      checked={tousLesCollaborateurs}
                      onChange={e => setTousLesCollaborateurs(e.target.checked)}
                    />
                    Afficher les collaborateurs des autres concessions
                  </label>
                </div>
              </div>

              <label className="auto-check">
                <input type="checkbox" checked={form.actif} onChange={e => setForm(f => ({ ...f, actif: e.target.checked }))} />
                Règle active
              </label>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
              <button className="btn-primary" disabled={saving} onClick={enregistrer}>
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Automatisation;
